import { ResponseTransformer } from '@lemsar/api/transformers/response';
import { Injectable } from '@angular/core';
import { LemsarApiClient } from '@lemsar/api/client';
import { UI } from '@shared/models/ui';
import { Observable } from 'rxjs/Observable';
import { StoreData } from '@shared/module/common/model/store-data';
import { Building, BuildingGallery, UploadBuildingGallery, VocationalBuilding } from '@lemsar/institution/domains/model/building';
import { Collection } from '@lemsar/common/domains/interface/collection';

/**
 *  @author     Arif Setianto <arifsetiantoo@gmail.com>
 *  @created    22/06/2018
 */
@Injectable()
export class BuildingService {
    private _ui: StoreData<UI>;
    private filterValue: string = '';

    public constructor(private api: LemsarApiClient) {
        this._ui = new StoreData<UI>(new UI());
    }

    public get ui$(): Observable<UI> {
        return this._ui.stream$;
    }

    public get ui(): UI {
        return this._ui.value;
    }

    public fetchInstitutionBuildings(id: string, type?: string, params?: any): Observable<Collection<Building>> {
        const queryParams = !params
            ? { page: 1, limit: 20 }
            : { page: params.page || 1, limit: params.limit || 20, group: params.group || [] };

        if (type && 'SEMUA' !== type) {
            this.filterValue = '?type[]=' + type;
        } else {
            this.filterValue = '';
        }

        return this.api
            .get<any>('institutions/' + id + '/buildings' + this.filterValue, queryParams)
            .pipe(ResponseTransformer.toCollection(Building));
    }

    public fetchInstitutionPublicBuildings(id: string, type?: string, params?: any): Observable<Collection<Building>> {
        const queryParams = !params
            ? { page: 1, limit: 20 }
            : { page: params.page || 1, limit: params.limit || 20, group: params.group || [] };

        if (type && 'SEMUA' !== type) {
            this.filterValue = '?type[]=' + type;
        } else {
            this.filterValue = '';
        }

        return this.api
            .get<any>('institutions/' + id + '/public-buildings' + this.filterValue, queryParams)
            .pipe(ResponseTransformer.toCollection(Building));
    }

    public fetchInstitutionBuildingById(institutionId: string, buildingId: string): Observable<Building> {
        return this.api
            .get<any>('institutions/' + institutionId + '/buildings/' + buildingId)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Building));
    }

    public createInstitutionBuilding(id: string, value: Building): Observable<Building> {
        return this.api
            .post<any>('institutions/' + id + '/buildings', value)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Building));
    }

    public createInstitutionVocationalBuilding(id: string, value: any): Observable<VocationalBuilding> {
        return this.api
            .post<any>('institutions/' + id + '/vocational-buildings', value)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(VocationalBuilding));
    }

    public updateInstitutionBuilding(institutionId: string, buildingId: string, value: Building): Observable<Building> {
        return this.api
            .patch<any>('institutions/' + institutionId + '/buildings/' + buildingId, value)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Building));
    }

    public updateInstitutionVocationalBuilding(institutionId: string, buildingId: string, value: any): Observable<VocationalBuilding> {
        return this.api
            .patch<any>('institutions/' + institutionId + '/vocational-buildings/' + buildingId, value)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(VocationalBuilding));
    }

    public deleteInstitutionBuilding(institutionId: string, buildingId: string): Observable<Building> {
        return this.api
            .delete<any>('institutions/' + institutionId + '/buildings/' + buildingId)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(Building));
    }

    public fetchInstitutionBuildingGalleries(id: string): Observable<BuildingGallery[]> {
        return this.api
            .get<any>('buildings/' + id + '/galleries')
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toArrayClass(BuildingGallery));
    }

    public uploadBuildingGalleries(value: UploadBuildingGallery, id: string): Observable<BuildingGallery> {
        const formData: any = new FormData();

        formData.append('image', value.image, value.image.name);
        formData.append('description', value.description);

        return this.api
            .post<any>('buildings/' + id + '/galleries', formData)
            .pipe(ResponseTransformer.toData(), ResponseTransformer.toSingleClass(BuildingGallery));
    }

    public deleteBuildingGallery(id: string): Observable<void> {
        return this.api.delete<any>('building-galleries/' + id).pipe(ResponseTransformer.toData());
    }
}



// WEBPACK FOOTER //
// ./src/lemsar/institution/domains/services/building.service.ts